import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import Button from "../ui/Button";
import ExpectedMaterialCostsSection from "../ProjectEconomy/ExpectedMaterialCostsSection";
import LineItemAssignmentToolbar from "./LineItemAssignmentToolbar";
import {
  getBillingLabel,
  getVisibleLineItemCount,
  getVisibleLineItemSum,
  hasPriceDiff,
  normalizeProjectId,
} from "../../utils/invoiceDisplayUtils";
import { getTaskLabel } from "../ProjectEconomy/expectedCostUtils";

export default function InvoiceReviewModal({
  invoice,
  onInvoiceChange,
  onClose,
  onSave,
  saveLoading = false,
  saveError = null,
  projects = [],
  taskBundle,
  selectedLineItemIds = [],
  onToggleLineItemSelection,
  onToggleAllLineItems,
  onClearLineItemSelection,
  onDeselectLineItem,
  lineItemTaskOverride = "",
  onLineItemTaskOverrideChange,
  lineItemBillingOption = "included",
  onLineItemBillingOptionChange,
  onApplyLineItemTaskAndBilling,
  lineItemApplySaving = false,
  onNewTask,
  onViewPdf,
  onOpenGmail,
  onReprocess,
  onDelete,
  deleteLoading = false,
  showGmailActions = true,
  showReprocess = true,
}) {
  const [lineModifyMenuOpen, setLineModifyMenuOpen] = useState(false);
  const lineModifyMenuRef = useRef(null);

  useEffect(() => {
    if (!invoice) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [invoice?._id]);

  useEffect(() => {
    if (!lineModifyMenuOpen) return undefined;
    const onMouseDown = (e) => {
      if (lineModifyMenuRef.current && !lineModifyMenuRef.current.contains(e.target)) {
        setLineModifyMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [lineModifyMenuOpen]);

  if (!invoice) return null;

  const projectIdStr = normalizeProjectId(invoice.projectId);
  const projectInList = projects.some((p) => String(p._id) === projectIdStr);

  const stripVatFromSelectedLineItems = () => {
    if (!invoice || selectedLineItemIds.length === 0) return;
    const selectedSet = new Set(selectedLineItemIds);
    onInvoiceChange({
      ...invoice,
      lineItems: (invoice.lineItems || []).map((item) => {
        if (!selectedSet.has(item._id)) return item;
        const currentNet = Number(item.net);
        if (item.net == null || Number.isNaN(currentNet)) return item;
        const newNet = currentNet / 1.25;
        return { ...item, net: Math.round(newNet * 100) / 100 };
      }),
    });
    setLineModifyMenuOpen(false);
  };

  const handleClose = () => {
    setLineModifyMenuOpen(false);
    onClearLineItemSelection?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">Invoice review</h3>
            <select
              value={invoice.status === "done" ? "reviewed" : (invoice.status || "new")}
              onChange={(e) => onInvoiceChange({ ...invoice, status: e.target.value })}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="new">New</option>
              <option value="in_review">In review</option>
              <option value="reviewed">Reviewed</option>
              <option value="invoiced">Invoiced</option>
            </select>
            {invoice.gmailIngestMessageId && onViewPdf && (
              <Button variant="outline" size="sm" onClick={() => onViewPdf(invoice._id)}>
                View PDF
              </Button>
            )}
          </div>
          <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-700">
            ×
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium">Project override</label>
            <select
              value={projectIdStr}
              onChange={(e) => {
                const pid = e.target.value;
                const proj = projects.find((p) => String(p._id) === pid);
                onInvoiceChange({
                  ...invoice,
                  projectId: pid || undefined,
                  projectCode: proj?.name ?? invoice.projectCode,
                });
              }}
              className="border rounded px-2 py-1"
            >
              <option value="">—</option>
              {!projectInList && projectIdStr && (
                <option value={projectIdStr}>
                  {invoice.projectCode || "Linked project"}
                </option>
              )}
              {projects.map((p) => (
                <option key={String(p._id)} value={String(p._id)}>
                  {p.name} {p.address ? `(${p.address})` : ""}
                </option>
              ))}
            </select>
          </div>

          {projectIdStr && (
            <ExpectedMaterialCostsSection
              projectId={projectIdStr}
              taskBundle={taskBundle}
              matchedInvoiceId={invoice._id}
              defaultCollapsed
            />
          )}

          <div className="grid grid-cols-1 gap-2">
            <label className="text-sm font-medium">Notes</label>
            <textarea
              value={invoice.notes || ""}
              onChange={(e) => onInvoiceChange({ ...invoice, notes: e.target.value })}
              className="border rounded px-2 py-1 w-full"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-cols-1 gap-1">
              <label className="text-sm font-medium">Invoice date</label>
              <input
                type="date"
                value={invoice.invoiceDate ? dayjs(invoice.invoiceDate).format("YYYY-MM-DD") : ""}
                onChange={(e) =>
                  onInvoiceChange({
                    ...invoice,
                    invoiceDate: e.target.value ? new Date(e.target.value) : undefined,
                  })
                }
                className="border rounded px-2 py-1"
              />
            </div>
            <div className="grid grid-cols-1 gap-1">
              <label className="text-sm font-medium">Supplier</label>
              <input
                type="text"
                value={invoice.supplierName || ""}
                onChange={(e) =>
                  onInvoiceChange({ ...invoice, supplierName: e.target.value || undefined })
                }
                className="border rounded px-2 py-1 w-full"
                placeholder="Supplier name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-cols-1 gap-1">
              <label className="text-sm font-medium">Invoice number</label>
              <input
                type="text"
                value={invoice.invoiceNumber || ""}
                onChange={(e) =>
                  onInvoiceChange({ ...invoice, invoiceNumber: e.target.value || undefined })
                }
                className="border rounded px-2 py-1 w-full"
                placeholder="Invoice number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid grid-cols-1 gap-1">
              <label className="text-sm font-medium">Net total</label>
              <input
                type="number"
                step="any"
                value={invoice.totals?.net ?? ""}
                onChange={(e) =>
                  onInvoiceChange({
                    ...invoice,
                    totals: {
                      ...(invoice.totals || {}),
                      net: e.target.value === "" ? undefined : Number(e.target.value),
                    },
                  })
                }
                className="border rounded px-2 py-1 w-full [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              />
            </div>
            <div className="grid grid-cols-1 gap-1">
              <label className="text-sm font-medium">Gross total</label>
              <input
                type="number"
                step="any"
                value={invoice.totals?.gross ?? ""}
                onChange={(e) =>
                  onInvoiceChange({
                    ...invoice,
                    totals: {
                      ...(invoice.totals || {}),
                      gross: e.target.value === "" ? undefined : Number(e.target.value),
                    },
                  })
                }
                className="border rounded px-2 py-1 w-full"
              />
            </div>
          </div>

          <div className="text-sm">
            <div className="font-medium mb-1 flex items-center justify-between flex-wrap gap-2">
              <span>Line items</span>
              <div className="flex items-center gap-2">
                <div className="relative" ref={lineModifyMenuRef}>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={selectedLineItemIds.length === 0}
                    onClick={() => setLineModifyMenuOpen((open) => !open)}
                    aria-haspopup="menu"
                    aria-expanded={lineModifyMenuOpen}
                  >
                    Modify ▾
                  </Button>
                  {lineModifyMenuOpen && selectedLineItemIds.length > 0 && (
                    <div className="absolute right-0 top-full mt-1 z-10 min-w-[120px] bg-white border border-gray-200 rounded shadow-lg py-1">
                      <button
                        type="button"
                        onClick={stripVatFromSelectedLineItems}
                        className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        -25%
                      </button>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const items = invoice.lineItems || [];
                    onInvoiceChange({
                      ...invoice,
                      lineItems: [
                        ...items,
                        {
                          _id: `temp-${Date.now()}-${items.length}`,
                          name: "",
                          articleNumber: "",
                          amount: undefined,
                          unit: "",
                          net: undefined,
                          gross: undefined,
                          billingDecision: "inherit",
                        },
                      ],
                    });
                  }}
                >
                  + Add line
                </Button>
              </div>
            </div>

            <LineItemAssignmentToolbar
              selectedCount={selectedLineItemIds.length}
              taskBundle={taskBundle}
              taskOverride={lineItemTaskOverride}
              onTaskOverrideChange={onLineItemTaskOverrideChange}
              billingOption={lineItemBillingOption}
              onBillingOptionChange={onLineItemBillingOptionChange}
              onApply={() => onApplyLineItemTaskAndBilling?.(invoice._id, selectedLineItemIds)}
              onClearSelection={onClearLineItemSelection}
              onNewTask={onNewTask}
              applySaving={lineItemApplySaving}
              projectId={projectIdStr}
              size="sm"
            />

            <table className="min-w-full border text-sm">
              <thead>
                <tr>
                  <th className="text-left border p-1 w-8">
                    <input
                      type="checkbox"
                      checked={
                        (invoice.lineItems || []).length > 0 &&
                        selectedLineItemIds.length === (invoice.lineItems || []).length
                      }
                      onChange={() => onToggleAllLineItems?.(invoice.lineItems)}
                      className="rounded border-gray-300"
                      title="Select all line items"
                    />
                  </th>
                  <th className="text-left border p-1">Article #</th>
                  <th className="text-left border p-1">Name</th>
                  <th className="text-left border p-1">Billing</th>
                  <th className="text-left border p-1">Task</th>
                  <th className="text-right border p-1 pr-4">Amount</th>
                  <th className="text-left border p-1">Unit</th>
                  <th className="text-right border p-1">Net</th>
                  <th className="text-left border p-1 w-16" />
                </tr>
              </thead>
              <tbody>
                {(invoice.lineItems || []).map((li, idx) => {
                  const selectedReview = selectedLineItemIds.includes(li._id);
                  const isHidden = li.billingDecision === "hide_from_project";
                  const updateLineItem = (field, value) => {
                    onInvoiceChange({
                      ...invoice,
                      lineItems: (invoice.lineItems || []).map((item, i) =>
                        i === idx ? { ...item, [field]: value } : item
                      ),
                    });
                  };
                  return (
                    <tr
                      key={li._id || idx}
                      className={`${selectedReview ? "bg-blue-50" : ""} ${isHidden ? "opacity-70" : ""}`}
                    >
                      <td className="border p-1">
                        <input
                          type="checkbox"
                          checked={selectedReview}
                          onChange={() => onToggleLineItemSelection?.(li._id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        <input
                          type="text"
                          value={li.articleNumber ?? ""}
                          onChange={(e) => updateLineItem("articleNumber", e.target.value || undefined)}
                          className="border rounded px-1 py-0.5 w-full max-w-[80px]"
                        />
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        <input
                          type="text"
                          value={li.name ?? ""}
                          onChange={(e) => updateLineItem("name", e.target.value || "")}
                          className="border rounded px-1 py-0.5 w-full min-w-[100px]"
                        />
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        {getBillingLabel(li.billingDecision)}
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        {getTaskLabel(li.taskOverride, taskBundle)}
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        <input
                          type="number"
                          step="any"
                          value={li.amount ?? ""}
                          onChange={(e) =>
                            updateLineItem(
                              "amount",
                              e.target.value === "" ? undefined : Number(e.target.value)
                            )
                          }
                          className="border rounded px-1 py-0.5 w-full max-w-[70px] text-right [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        <input
                          type="text"
                          value={li.unit ?? ""}
                          onChange={(e) => updateLineItem("unit", e.target.value || undefined)}
                          className="border rounded px-1 py-0.5 w-full max-w-[60px]"
                        />
                      </td>
                      <td className={`border p-1 ${isHidden ? "line-through text-gray-500" : ""}`}>
                        <input
                          type="number"
                          step="any"
                          value={li.net ?? ""}
                          onChange={(e) =>
                            updateLineItem(
                              "net",
                              e.target.value === "" ? undefined : Number(e.target.value)
                            )
                          }
                          className="border rounded px-1 py-0.5 w-full max-w-[80px] text-right [appearance:textfield] [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                      </td>
                      <td className="border p-1">
                        <button
                          type="button"
                          onClick={() => {
                            const id = li._id;
                            onInvoiceChange({
                              ...invoice,
                              lineItems: (invoice.lineItems || []).filter((_, i) => i !== idx),
                            });
                            onDeselectLineItem?.(li._id);
                          }}
                          className="text-red-600 hover:text-red-800 text-xs"
                          title="Remove line"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-200 font-medium">
                  <td className="border p-1" colSpan={8}>
                    {getVisibleLineItemCount(invoice.lineItems)} line item
                    {getVisibleLineItemCount(invoice.lineItems) !== 1 ? "s" : ""}
                    {getVisibleLineItemCount(invoice.lineItems) !== (invoice.lineItems || []).length && (
                      <span className="text-gray-500 ml-1">
                        ({(invoice.lineItems || []).length - getVisibleLineItemCount(invoice.lineItems)} hidden)
                      </span>
                    )}
                    {hasPriceDiff(invoice) && (
                      <span className="ml-2 inline-flex px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">
                        Price diff
                      </span>
                    )}
                    {hasPriceDiff(invoice) && (
                      <button
                        type="button"
                        onClick={() => {
                          const lineSum = getVisibleLineItemSum(invoice.lineItems);
                          onInvoiceChange({
                            ...invoice,
                            totals: {
                              ...(invoice.totals || {}),
                              net: lineSum,
                              gross: lineSum * 1.25,
                            },
                          });
                        }}
                        className="ml-2 text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Recalc totals from lines
                      </button>
                    )}
                  </td>
                  <td className="border p-1 text-right">
                    {getVisibleLineItemSum(invoice.lineItems).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {saveError && <div className="px-4 pb-2 text-sm text-red-600">{saveError}</div>}

        <div className="p-4 border-t flex flex-wrap gap-2">
          <Button onClick={onSave} disabled={saveLoading}>
            {saveLoading ? "Saving…" : "Save"}
          </Button>
          {invoice.gmailIngestMessageId && onViewPdf && (
            <Button variant="outline" onClick={() => onViewPdf(invoice._id)}>
              View PDF
            </Button>
          )}
          {showGmailActions && invoice.source?.gmailMessageId && onOpenGmail && (
            <Button variant="outline" onClick={() => onOpenGmail(invoice.source.gmailMessageId)}>
              Open in Gmail
            </Button>
          )}
          {showReprocess && onReprocess && (
            <>
              <Button variant="outline" onClick={() => onReprocess(invoice._id)}>
                Reprocess
              </Button>
              <span
                className="text-xs text-gray-500 self-center"
                title="Reprocess will replace all extracted data, including any manual edits."
              >
                Reprocess replaces all data.
              </span>
            </>
          )}
          {onDelete && (
            <Button
              variant="danger"
              onClick={() => onDelete(invoice)}
              disabled={deleteLoading || saveLoading}
            >
              {deleteLoading ? "Deleting…" : "Delete"}
            </Button>
          )}
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
